import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from 'react-bootstrap';
import PriceConfirmation from '../Global/PriceConfirmation';
import ProductSelector from '../Global/ProductSelector';
import FavoriteDropdown from '../Global/FavoriteDropdown';
import SpecialProductRow from './SpecialProductRow';

import {
  getFurnitureTypes,
  getFurnitureModelsByTypeId,
  getModelColorsByModelId,
  getModelSizesByModelId,
  getProductPrice,
  getProductVariantsByTypeId,
  getAvailableModelColors,
  getAvailableModelSizes
} from '../../api';

// Definir el componente EditItemsInOrder
const EditItemsInOrderComponent = ({
  initialItems = [],
  onItemsChange,
  onOrderMileageChange,
  initialMileage = 0,
  initialSpecialProducts = [],
  initialPrepaidAmount = null,
  onPrepaidAmountChange,
  onSave,
  onCancel,
  saveLabel = 'Guardar Cambios',
}) => {
  // Usar useRef para mantener una referencia estable a los valores iniciales
  const initialItemsRef = useRef(initialItems);
  const initialSpecialProductsRef = useRef(initialSpecialProducts);
  const initialMileageRef = useRef(initialMileage);
  
  const [orderItems, setOrderItems] = useState(initialItemsRef.current);
  const [specialProducts, setSpecialProducts] = useState(initialSpecialProductsRef.current);
  const [newItemCounter, setNewItemCounter] = useState(0);
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [furnitureModels, setFurnitureModels] = useState({});
  const [modelColors, setModelColors] = useState({});
  const [modelSizes, setModelSizes] = useState({});
  const [variants, setVariants] = useState({});
  const [orderMileage, setOrderMileage] = useState(initialMileageRef.current); // Kilometraje de la orden
  const [manualPrices, setManualPrices] = useState({});
  const [basePrices, setBasePrices] = useState({}); // Precios base sin variantes
  const [newSpecialProduct, setNewSpecialProduct] = useState({
    description: '',
    price: 0,
    quantity: 1,
    productTypeId: null
  });
  const [validationError, setValidationError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [prepaidAmount, setPrepaidAmount] = useState(initialPrepaidAmount);
  const prepaidAmountRef = useRef(initialPrepaidAmount);

  // Verificar si todos los productos están completos
  const areProductsComplete = useCallback(() => {
    // Verificar productos normales
    const normalProductsComplete = orderItems.every(item => {
      return (
        item.productPrice?.typeId && 
        item.productPrice?.modelId && 
        item.productPrice?.colorId && 
        item.productPrice?.sizeId
      );
    });
    
    // Verificar productos especiales
    const specialProductsComplete = specialProducts.every(product => 
      product.description && product.description.trim() !== '' && !!product.productTypeId
    );
    
    return normalProductsComplete && specialProductsComplete;
  }, [orderItems, specialProducts]);
  
  // Verificar si todos los productos están completos y tienen valores válidos
  const areAllProductsComplete = useCallback(() => {
    // Verificar productos normales
    const normalProductsComplete = orderItems.every(item => {
      return (
        item.productPrice?.typeId && 
        item.productPrice?.modelId && 
        item.productPrice?.colorId && 
        item.productPrice?.sizeId && 
        item.unitPrice > 0 && 
        item.quantity > 0
      );
    });
    
    // Verificar productos especiales
    const specialProductsComplete = specialProducts.every(product => 
      product.description && 
      product.description.trim() !== '' && 
      !!product.productTypeId &&
      product.price > 0 && 
      product.quantity > 0
    );
    
    return normalProductsComplete && specialProductsComplete;
  }, [orderItems, specialProducts]);

  const setManualPriceForItem = (itemId, value) => {
    setManualPrices(prev => ({ ...prev, [itemId]: value }));
    // Actualizar precio base cuando se edita manualmente
    const item = orderItems.find(i => i.id === itemId);
    if (item) {
      const typeVariants = variants[item.productPrice.typeId] || [];
      const currentVariantPrice = typeVariants
        .filter(v => (item.selectedVariants || []).includes(v.id))
        .reduce((sum, v) => sum + v.priceDelta, 0);
      setBasePrices(prev => ({ ...prev, [itemId]: value - currentVariantPrice }));
      
      // Actualizar el precio unitario del item preservando la cantidad
      setOrderItems(prev => prev.map(prevItem => 
        prevItem.id === itemId 
          ? { ...prevItem, unitPrice: value }
          : prevItem
      ));
    }
  };

  // Referencias para callbacks
  const onItemsChangeRef = useRef(onItemsChange);
  const onOrderMileageChangeRef = useRef(onOrderMileageChange);
  
  // Actualizar referencias cuando cambian las props
  useEffect(() => {
    onItemsChangeRef.current = onItemsChange;
    onOrderMileageChangeRef.current = onOrderMileageChange;
  }, [onItemsChange, onOrderMileageChange]);

  // Inicializar precios manuales con los precios existentes
  useEffect(() => {
    const initialManualPrices = {};
    initialItems.forEach(item => {
      if (item.unitPrice) {
        initialManualPrices[item.id] = item.unitPrice;
      }
    });
    setManualPrices(initialManualPrices);
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (initialMileage !== undefined) {
      setOrderMileage(initialMileage);
    }
    
    // Cargar los tipos de muebles al inicio
    getFurnitureTypes(false)
      .then((types) => {
        setFurnitureTypes(types);

        // Cargar dependencias para elementos iniciales
        if (initialItems && initialItems.length > 0) {
          initialItems.forEach((item) => {
            if (item.productPrice?.typeId) {
              Promise.all([
                getFurnitureModelsByTypeId(item.productPrice.typeId, false),
                getProductVariantsByTypeId(item.productPrice.typeId, false)
              ]).then(([models, typeVariants]) => {
                setFurnitureModels((prev) => ({
                  ...prev,
                  [item.productPrice.typeId]: models,
                }));
                setVariants((prev) => ({
                  ...prev,
                  [item.productPrice.typeId]: typeVariants,
                }));

                if (item.productPrice.modelId) {
                  getModelColorsByModelId(item.productPrice.modelId, false).then((colors) => {
                    setModelColors((prev) => ({
                      ...prev,
                      [item.productPrice.modelId]: colors,
                    }));
                  });

                  getModelSizesByModelId(item.productPrice.modelId, false).then((sizes) => {
                    setModelSizes((prev) => ({
                      ...prev,
                      [item.productPrice.modelId]: sizes,
                    }));
                  });
                }
              });
            }
          });
          
          // Inicializar selectedVariants desde orderItemVariants
          setOrderItems(prev => prev.map(item => ({
            ...item,
            selectedVariants: item.orderItemVariants?.map(v => v.variantId) || []
          })));
        }
      })
      .catch(console.error);
  }, []);  // Ejecutar solo una vez al montar el componente

  // Notificar cambios en los items
  const prevOrderItemsRef = useRef();
  const prevSpecialProductsRef = useRef();
  
  useEffect(() => {
    // Evitar notificaciones en el primer render
    if (!prevOrderItemsRef.current) {
      prevOrderItemsRef.current = [...orderItems];
      prevSpecialProductsRef.current = [...specialProducts];
      return;
    }
    
    if (onItemsChangeRef.current) {
      const cleanedItems = orderItems.map(item => ({
        id:                item.id,
        orderId:           item.orderId,
        quantity:          item.quantity,
        unitPrice:         manualPrices[item.id] ?? item.unitPrice,
        productPriceId:    item.productPrice?.id,
        selectedVariants:  item.selectedVariants || [],
        
        productPrice: {
          id:                        item.productPrice?.id,
          typeId:                    item.productPrice?.typeId,
          modelId:                   item.productPrice?.modelId,
          colorId:                   item.productPrice?.colorId,
          sizeId:                    item.productPrice?.sizeId,
          price:                     item.productPrice?.price,
          commissionPercentageModifier: item.productPrice?.commissionPercentageModifier,
          // Incluir category y categoryId
          category:                  item.productPrice?.category || item.productPrice?.type?.category,
          categoryId:                item.productPrice?.categoryId || item.productPrice?.type?.categoryId
        }
      }));
      
      onItemsChangeRef.current(cleanedItems, specialProducts);
      
      // Actualizar referencias
      prevOrderItemsRef.current = [...orderItems];
      prevSpecialProductsRef.current = [...specialProducts];
    }
  }, [orderItems, specialProducts, manualPrices]);

  // Notificar cambios en el kilometraje
  const prevMileageRef = useRef();
  
  useEffect(() => {
    // Evitar notificaciones en el primer render
    if (prevMileageRef.current === undefined) {
      prevMileageRef.current = orderMileage;
      return;
    }
    
    if (onOrderMileageChangeRef.current && prevMileageRef.current !== orderMileage) {
      onOrderMileageChangeRef.current(orderMileage);
      prevMileageRef.current = orderMileage;
    }
  }, [orderMileage]);

  // Notificar cambios en la validación
  const prevValidationRef = useRef();
  
  useEffect(() => {
    if (onItemsChangeRef.current) {
      const isValid = areProductsComplete();
      
      // Evitar notificaciones en el primer render o si no hay cambios
      if (prevValidationRef.current === undefined || prevValidationRef.current !== isValid) {
        onItemsChangeRef.current(orderItems, specialProducts);
        prevValidationRef.current = isValid;
      }
    }
  }, [orderItems, specialProducts, areProductsComplete]);

  const handleItemChange = (itemId, field, value) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Si se cambia el precio unitario, actualizar el precio base
          if (field === 'unitPrice') {
            const typeVariants = variants[item.productPrice.typeId] || [];
            const currentVariantPrice = typeVariants
              .filter(v => (item.selectedVariants || []).includes(v.id))
              .reduce((sum, v) => sum + v.priceDelta, 0);
            setBasePrices(prev => ({ ...prev, [itemId]: value - currentVariantPrice }));
          }
          
          return updatedItem;
        }
        return item;
      })
    );
  };

  // Cambiar el id cuando el campo pierda el foco (onBlur)
  const handleBlur = (itemId) => {
    if (!String(itemId).startsWith('new-')) {
      setNewItemCounter((prev) => prev + 1);
      setOrderItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, id: `new-${newItemCounter}` }
            : item
        )
      );
    }
  };

  const handleAddItem = async () => {
    const newId = `new-${newItemCounter}`;
    setNewItemCounter((prev) => prev + 1);
  
    let productType = null;
    let model = null;
    let color = null;
    let size = null;
    let unitPrice = 0;
  
    try {
      // Autoselect tipo
      const types = furnitureTypes.length > 0 ? furnitureTypes : await getFurnitureTypes(false);
      const autoType = types.length === 1 ? types[0] : null;
  
      if (autoType) {
        productType = autoType;
      }
  
      // Autoselect modelo
      if (productType) {
        const models = await getFurnitureModelsByTypeId(productType.id, false);
        setFurnitureModels((prev) => ({ ...prev, [productType.id]: models }));
        model = models.length === 1 ? models[0] : null;
      }
  
      // Autoselect color y tamaño
      if (model) {
        const colors = await getModelColorsByModelId(model.id, false);
        const sizes = await getModelSizesByModelId(model.id, false);
        setModelColors((prev) => ({ ...prev, [model.id]: colors }));
        setModelSizes((prev) => ({ ...prev, [model.id]: sizes }));
  
        color = colors.length === 1 ? colors[0] : null;
        size = sizes.length === 1 ? sizes[0] : null;
      }
  
      // Obtener precio si todos están definidos
      if (productType && model && color && size) {
        unitPrice = (await import('../../api').then(({ getProductPrice }) =>
          getProductPrice({
            typeId: productType.id,
            modelId: model.id,
            colorId: color.id,
            sizeId: size.id,
          })).price
        );
      }
    } catch (err) {
      console.error('Error al autocompletar nuevo ítem:', err);
    }
  
    const newItem = {
      id: newId,
      productType,
      model,
      color,
      size,
      quantity: 1,
      unitPrice: unitPrice || 0,
      productPriceId: 0,
      productPrice: {
        id: null,
        typeId:  productType?.id ?? null,
        modelId: model?.id       ?? null,
        colorId: color?.id       ?? null,
        sizeId:  size?.id        ?? null,
      }
    };
  
    setOrderItems((prev) => [...prev, newItem]);
  };
  
  const updateItemPriceIfReady = async (item) => {
    const typeId = item.productType?.id || item.productPrice?.typeId;
    const modelId = item.model?.id || item.productPrice?.modelId;
    const colorId = item.color?.id || item.productPrice?.colorId;
    const sizeId = item.size?.id || item.productPrice?.sizeId;
    if (typeId && modelId && colorId && sizeId) {
      try {
        const priceRes = await getProductPrice({
          typeId:  typeId,
          modelId: modelId,
          colorId: colorId,
          sizeId:  sizeId,
        });

        const newPrice = priceRes.price;
        if (newPrice !== undefined && newPrice !== null) {
          // Actualizar tanto el estado orderItems como manualPrices
          setOrderItems(prev =>
            prev.map(i => {
              if (i.id !== item.id) return i;
              return {
                ...i,
                unitPrice: newPrice,
                productPriceId: priceRes.id ?? i.productPrice.id,
                productPrice: {
                  ...i.productPrice,
                  id:      priceRes.id ?? i.productPrice.id,
                  typeId:  typeId,
                  modelId: modelId,
                  colorId: colorId,
                  sizeId:  sizeId,
                }
              };
            })
          );
          
          // Actualizar manualPrices para que se refleje en el input
          setManualPrices(prev => ({ ...prev, [item.id]: newPrice }));
        }
      } catch (err) {
        console.error('❌ Error al obtener precio:', err);
      }
    }
  };

  const autoSelectModelDetails = async (itemId, selectedType, models) => {
    if (models.length !== 1) return;
  
    const autoModel = models[0];
    handleItemChange(itemId, 'model', autoModel);
    handleItemChange(itemId, 'color', null);
    handleItemChange(itemId, 'size', null);

    handleItemChange(itemId, 'productPrice', {
      ...orderItems.find(i => i.id === itemId).productPrice,
      typeId:  selectedType?.id ?? null,
      modelId: autoModel.id,
      colorId: null,
      sizeId:  null
    });
  
    try {
      const [colors, sizes] = await Promise.all([
        getModelColorsByModelId(autoModel.id, false),
        getModelSizesByModelId(autoModel.id, false),
      ]);
  
      setModelColors((prev) => ({ ...prev, [autoModel.id]: colors }));
      setModelSizes((prev) => ({ ...prev, [autoModel.id]: sizes }));
  
      let updatedColor = null;
      let updatedSize = null;

      if (colors.length === 1) {
        updatedColor = colors[0];
        handleItemChange(itemId, 'color', updatedColor);
        handleItemChange(itemId, 'productPrice', {
          ...orderItems.find(i => i.id === itemId).productPrice,
          typeId:  selectedType?.id ?? null,
          modelId: autoModel.id,
          colorId: updatedColor.id
        });
      }
  
      if (sizes.length === 1) {
        updatedSize = sizes[0];
        handleItemChange(itemId, 'size', updatedSize);
        handleItemChange(itemId, 'productPrice', {
          ...orderItems.find(i => i.id === itemId).productPrice,
          typeId:  selectedType?.id ?? null,
          modelId: autoModel.id,
          colorId: updatedColor?.id,
          sizeId:  updatedSize.id
        });
      }
  
      const updatedItem = orderItems.find((i) => i.id === itemId);
      updateItemPriceIfReady({
        ...updatedItem,
        productType: selectedType,
        model: autoModel,
        color: updatedColor,
        size: updatedSize,
      });
    } catch (err) {
      console.error('Error al autocompletar detalles del modelo:', err);
    }
  };
  

  const handleRemoveItem = (itemId) => {
    setOrderItems((prev) => prev.filter((item) => item.id !== itemId));
  };
  
  // Modificar handleUpdateSpecialProduct para notificar inmediatamente al padre
  const handleUpdateSpecialProduct = (id, data) => {
    setSpecialProducts(prev => {
      const updated = prev.map(product =>
        product.id === id ? { ...product, ...data } : product
      );
      // Notificar inmediatamente al padre
      if (onItemsChangeRef.current) {
        onItemsChangeRef.current(orderItems, updated);
      }
      return updated;
    });
  };

  // Modificar handleAddSpecialProduct para asignar un id único temporal
  const handleAddSpecialProduct = () => {
    setSpecialProducts(prev => {
      // Buscar el id máximo actual (numérico o string)
      let maxId = 0;
      prev.forEach(p => {
        const n = typeof p.id === 'number' ? p.id : parseInt((p.id || '').replace('temp-', ''));
        if (!isNaN(n) && n > maxId) maxId = n;
      });
      const newId = `temp-${maxId + 1}`;
      return [
        ...prev,
        {
          id: newId,
          description: '',
          price: 0,
          quantity: 1,
          productTypeId: null
        }
      ];
    });
  };
  
  const handleDeleteSpecialProduct = (id) => {
    setSpecialProducts(prev => prev.filter(product => product.id !== id));
  };
  
  const handleSpecialProductChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = name === 'price' || name === 'quantity' ? Number(value) : value;
    
    // Actualizar el estado local inmediatamente
    setNewSpecialProduct(prev => ({
      ...prev,
      [name]: updatedValue
    }));
  };

  const handleVariantToggle = (itemId, variantId) => {
    setOrderItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      
      const currentVariants = item.selectedVariants || [];
      const newVariants = currentVariants.includes(variantId)
        ? currentVariants.filter(id => id !== variantId)
        : [...currentVariants, variantId];
      
      // Recalcular precio con variantes
      const typeVariants = variants[item.productPrice.typeId] || [];
      
      // Usar precio base almacenado o calcularlo desde el precio actual
      const storedBasePrice = basePrices[item.id];
      let basePriceWithoutVariants;
      
      if (storedBasePrice !== undefined) {
        basePriceWithoutVariants = storedBasePrice;
      } else {
        const currentVariantPrice = typeVariants
          .filter(v => currentVariants.includes(v.id))
          .reduce((sum, v) => sum + v.priceDelta, 0);
        basePriceWithoutVariants = (item.unitPrice || 0) - currentVariantPrice;
        setBasePrices(prev => ({ ...prev, [item.id]: basePriceWithoutVariants }));
      }
      
      // Calcular nuevo precio con nuevas variantes
      const newVariantPrice = typeVariants
        .filter(v => newVariants.includes(v.id))
        .reduce((sum, v) => sum + v.priceDelta, 0);
      
      const newPrice = basePriceWithoutVariants + newVariantPrice;
      
      // Actualizar el precio manual para que se refleje visualmente
      setManualPrices(prev => ({ ...prev, [itemId]: newPrice }));
      
      return {
        ...item,
        selectedVariants: newVariants,
        unitPrice: newPrice
      };
    }));
  };

  const handleProductTypeChange = async (itemId, typeId) => {
    const selectedType = furnitureTypes.find((type) => type.id === parseInt(typeId));
  
    handleItemChange(itemId, 'productType', selectedType || null);
    handleItemChange(itemId, 'model', null);
    handleItemChange(itemId, 'color', null);
    handleItemChange(itemId, 'size', null);
    handleItemChange(itemId, 'productPrice', {
      ...orderItems.find(i => i.id === itemId).productPrice,
      typeId:  selectedType?.id ?? null,
      type: selectedType || null,
      modelId: null,
      colorId: null,
      sizeId:  null
    });
  
    if (!selectedType) return;
  
    try {
      const [models, typeVariants] = await Promise.all([
        getFurnitureModelsByTypeId(selectedType.id),
        getProductVariantsByTypeId(selectedType.id, false)
      ]);
      
      setFurnitureModels((prev) => ({ ...prev, [selectedType.id]: models }));
      setVariants((prev) => ({ ...prev, [selectedType.id]: typeVariants }));
  
      await autoSelectModelDetails(itemId, selectedType, models);
    } catch (err) {
      console.error('Error al obtener modelos o variantes:', err);
    }
  
    const updatedItem = orderItems.find((item) => item.id === itemId);
    updateItemPriceIfReady({
      ...updatedItem,
      productType: selectedType,
      model: null,
      color: null,
      size: null,
    });
  };
  
  const handleModelChange = async (itemId, modelId) => {
    const item = orderItems.find((item) => item.id === itemId);
    const selectedModel = furnitureModels[item.productPrice.typeId]?.find(
      (model) => model.id === parseInt(modelId)
    );
  
    handleItemChange(itemId, 'model', selectedModel || null);
    handleItemChange(itemId, 'color', null);
    handleItemChange(itemId, 'size', null);
    handleItemChange(itemId, 'productPrice', {
      ...item.productPrice,
      modelId: selectedModel?.id ?? null,
      model: selectedModel || null,
      colorId: null,
      sizeId:  null
    });
  
    if (!selectedModel) {
      updateItemPriceIfReady({ ...item, model: null, color: null, size: null });
      return;
    }
  
    try {
      const [colors, sizes] = await Promise.all([
        getModelColorsByModelId(selectedModel.id, false),
        getModelSizesByModelId(selectedModel.id, false),
      ]);
  
      setModelColors((prev) => ({ ...prev, [selectedModel.id]: colors }));
      setModelSizes((prev) => ({ ...prev, [selectedModel.id]: sizes }));
  
      let updatedColor = null;
      let updatedSize = null;
  
      if (colors.length === 1) {
        updatedColor = colors[0];
        handleItemChange(itemId, 'color', updatedColor);
      }
  
      if (sizes.length === 1) {
        updatedSize = sizes[0];
        handleItemChange(itemId, 'size', updatedSize);
      }
  
      updateItemPriceIfReady({
        ...item,
        model: selectedModel,
        color: updatedColor,
        size: updatedSize,
      });
    } catch (err) {
      console.error('Error al cargar color o tamaño:', err);
    }
  };

  const handleColorChange = async (itemId, colorId) => {
    const item = orderItems.find((item) => item.id === itemId);
    const selectedColor = modelColors[item.productPrice.modelId]?.find(
      (color) => color.id === parseInt(colorId)
    );
    handleItemChange(itemId, 'color', selectedColor || null);
    handleItemChange(itemId, 'productPrice', {
      ...item.productPrice,
      colorId: selectedColor?.id ?? null,
      color: selectedColor || null
    });

    // Actualizar tamaños disponibles
    if (colorId && item.productPrice.typeId && item.productPrice.modelId) {
      try {
        const availableSizes = await getAvailableModelSizes(item.productPrice.typeId, item.productPrice.modelId, colorId, false);
        setModelSizes(prev => ({ ...prev, [item.productPrice.modelId]: availableSizes }));
      } catch (error) {
        console.error('Error loading available sizes:', error);
      }
    }

    updateItemPriceIfReady({ ...item, color: selectedColor });
  };

  const handleSizeChange = async (itemId, sizeId) => {
    const item = orderItems.find((item) => item.id === itemId);
    const selectedSize = modelSizes[item.productPrice.modelId]?.find(
      (size) => size.id === parseInt(sizeId)
    );
    handleItemChange(itemId, 'size', selectedSize || null);
    handleItemChange(itemId, 'productPrice', {
      ...item.productPrice,
      sizeId: selectedSize?.id ?? null,
      size: selectedSize || null
    });

    // Actualizar colores disponibles
    if (sizeId && item.productPrice.typeId && item.productPrice.modelId) {
      try {
        const availableColors = await getAvailableModelColors(item.productPrice.typeId, item.productPrice.modelId, sizeId, false);
        setModelColors(prev => ({ ...prev, [item.productPrice.modelId]: availableColors }));
      } catch (error) {
        console.error('Error loading available colors:', error);
      }
    }

    updateItemPriceIfReady({ ...item, size: selectedSize });
  };
  
  const handleProductSelection = (itemId, selection) => {
    const item = orderItems.find((item) => item.id === itemId);
    
    // Actualizar el producto con la selección
    handleItemChange(itemId, 'productPrice', {
      ...item.productPrice,
      typeId: selection.typeId || null,
      modelId: selection.modelId || null,
      colorId: selection.colorId || null,
      sizeId: selection.sizeId || null,
      id: selection.productPriceId || item.productPrice.id
    });
    
    // Si tenemos un precio, actualizarlo
    if (selection.price !== null) {
      handleItemChange(itemId, 'unitPrice', selection.price);
    }
    
    // Cargar variantes si hay un tipo seleccionado
    if (selection.typeId) {
      getProductVariantsByTypeId(selection.typeId, false)
        .then(typeVariants => {
          setVariants(prev => ({
            ...prev,
            [selection.typeId]: typeVariants
          }));
        })
        .catch(err => console.error('Error al cargar variantes:', err));
    }
  };

  // Calcular el total de los artículos
  const calculateTotalItems = () => {
    // Sumar el total de los productos normales
    const normalItemsTotal = orderItems.reduce((total, item) => {
      return total + item.quantity * item.unitPrice;
    }, 0);
    
    // Sumar el total de los productos especiales
    const specialItemsTotal = specialProducts.reduce((total, item) => {
      return total + (item.quantity || 1) * (item.price || 0);
    }, 0);
    
    return normalItemsTotal + specialItemsTotal;
  };

  // Calcular el costo del kilometraje
  const calculateMileageCost = () => {
    return Number(orderMileage) * 1.2 - 40;
  };

  // Calcular el importe total
  const calculateTotal = () => {
    const totalItems = calculateTotalItems();
    const mileageCost = calculateMileageCost();
    return totalItems + (mileageCost > 0 ? mileageCost : 0);
  };

  // Validación de productos
  const isValid = useCallback(() => {
    const normalProductsComplete = orderItems.every(item =>
      item.productPrice?.typeId &&
      item.productPrice?.modelId &&
      item.productPrice?.colorId &&
      item.productPrice?.sizeId
    );
    const specialProductsComplete = specialProducts.every(product =>
      product.description && product.description.trim() !== '' && !!product.productTypeId
    );
    return normalProductsComplete && specialProductsComplete;
  }, [orderItems, specialProducts]);

  useEffect(() => {
    if (!isValid()) {
      setValidationError('Todos los productos normales deben tener tipo, modelo, color y tamaño. Los productos especiales deben tener descripción.');
    } else {
      setValidationError('');
    }
  }, [isValid]);

  // Handler para guardar
  const handleSave = async () => {
    if (!isValid()) return;
    setIsSaving(true);
    // Normalizar ids: si no es número positivo, poner 0
    const normalizedOrderItems = orderItems.map(item => {
      // Extraer propiedades que no deben estar fuera de productPrice
      const {
        typeId, modelId, colorId, sizeId,
        type, model, color, size,
        productPrice = {}, ...rest
      } = item;
      // Obtener el precio directamente del input DOM
      const priceInput = document.getElementById(`price-${item.id}`);
      const inputValue = priceInput ? parseFloat(priceInput.value) : item.unitPrice;
      const finalUnitPrice = !isNaN(inputValue) ? inputValue : item.unitPrice;
      const resolvedProductPrice = {
          ...productPrice,
          typeId: productPrice.typeId || typeId || null,
          modelId: productPrice.modelId || modelId || null,
          colorId: productPrice.colorId || colorId || null,
          sizeId: productPrice.sizeId || sizeId || null,
          type: productPrice.type || item.productType || type || null,
          model: productPrice.model || item.model || model || null,
          color: productPrice.color || item.color || color || null,
          size: productPrice.size || item.size || size || null,
          category: productPrice.category || productPrice.type?.category || item.productType?.category || null,
          categoryId: productPrice.categoryId || productPrice.type?.categoryId || item.productType?.categoryId || null
        };
      return {
        ...rest,
        id: typeof item.id === 'number' && item.id > 0 ? item.id : 0,
        unitPrice: finalUnitPrice || 0,
        productPriceId: resolvedProductPrice.id || rest.productPriceId || 0,
        productPrice: resolvedProductPrice
      };
    });
    const normalizedSpecialProducts = specialProducts.map(product => ({
      ...product,
      id: typeof product.id === 'number' && product.id > 0 ? product.id : 0
    }));
    await onSave(normalizedOrderItems, normalizedSpecialProducts, orderMileage, prepaidAmountRef.current);
    setIsSaving(false);
  };

  // Handler para cancelar
  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <div className="mt-4">
      <h2>Editar artículos</h2>
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th>Acción</th>
              <th>Tipo</th>
              <th>Modelo</th>
              <th>Color</th>
              <th>Tamaño</th>
              <th>Cantidad</th>
              <th>Precio Unitario</th>
            </tr>
          </thead>
          <tbody>
            {orderItems.map((item) => (
              <React.Fragment key={item.id}>
                <tr>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      Eliminar
                    </button>
                  </td>
                <td>
                  <FavoriteDropdown
                    value={item.productPrice.typeId || ''}
                    onChange={(e) => handleProductTypeChange(item.id, e.target.value)}
                    options={furnitureTypes.map(type => ({
                      id: type.id,
                      name: type.typeName,
                      isFavorite: type.isFavorite
                    }))}
                    placeholder="Tipo"
                    className="form-control-sm"
                    data-testid={`type-dropdown-${item.id}`}
                  />
                </td>
                <td>
                  <FavoriteDropdown
                    value={item.productPrice.modelId || ''}
                    onChange={(e) => handleModelChange(item.id, e.target.value)}
                    options={(furnitureModels[item.productPrice.typeId] || []).map(model => ({
                      id: model.id,
                      name: model.modelName,
                      isFavorite: model.isFavorite
                    }))}
                    placeholder="Modelo"
                    disabled={!item.productPrice.typeId}
                    className="form-control-sm"
                    data-testid={`model-dropdown-${item.id}`}
                  />
                </td>
                <td>
                  <FavoriteDropdown
                    value={item.productPrice.colorId || ''}
                    onChange={(e) => handleColorChange(item.id, e.target.value)}
                    options={(modelColors[item.productPrice.modelId] || []).map(color => ({
                      id: color.id,
                      name: color.colorName,
                      isFavorite: color.isFavorite
                    }))}
                    placeholder="Color"
                    disabled={!item.productPrice.modelId}
                    className="form-control-sm"
                    data-testid={`color-dropdown-${item.id}`}
                  />
                </td>
                <td>
                  <FavoriteDropdown
                    value={item.productPrice.sizeId || ''}
                    onChange={(e) => handleSizeChange(item.id, e.target.value)}
                    options={(modelSizes[item.productPrice.modelId] || []).map(size => ({
                      id: size.id,
                      name: size.size,
                      isFavorite: size.isFavorite
                    }))}
                    placeholder="Tamaño"
                    disabled={!item.productPrice.modelId}
                    className="form-control-sm"
                    data-testid={`size-dropdown-${item.id}`}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(item.id, 'quantity', Number(e.target.value))
                    }
                    className="form-control form-control-sm"
                    data-testid={`quantity-input-${item.id}`}
                  />
                </td>
                <td>
                  <PriceConfirmation
                    originalPrice={item.unitPrice}
                    manualPrice={manualPrices[item.id] ?? item.unitPrice}
                    setManualPrice={(val) => setManualPriceForItem(item.id, val)}
                    onConfirm={(confirmedPrice) => {
                      setManualPriceForItem(item.id, confirmedPrice);
                    }}
                    inputId={`price-${item.id}`}
                    className="form-control form-control-sm"
                    data-testid={`price-input-${item.id}`}
                  />
                  </td>
                </tr>
                {variants[item.productPrice.typeId] && variants[item.productPrice.typeId].length > 0 && (
                  <tr>
                    <td></td>
                    <td colSpan="6">
                      <div className="border p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <strong>Variantes disponibles:</strong>
                        <div className="mt-2">
                          {variants[item.productPrice.typeId].map(variant => (
                            <div key={variant.id} className="form-check" style={{ marginBottom: '5px' }}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`variant-${item.id}-${variant.id}`}
                                checked={(item.selectedVariants || []).includes(variant.id)}
                                onChange={() => handleVariantToggle(item.id, variant.id)}
                              />
                              <label className="form-check-label" htmlFor={`variant-${item.id}-${variant.id}`}>
                                {variant.name} (+{variant.priceDelta}€)
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {/* Productos especiales */}
            {specialProducts.map((product) => (
              <SpecialProductRow
                key={`special-${product.id}`}
                specialProduct={product}
                onUpdate={handleUpdateSpecialProduct}
                onDelete={handleDeleteSpecialProduct}
                furnitureTypes={furnitureTypes}
              />
            ))}
            
            
            <tr>
              <td colSpan="7" className="text-center">
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleAddItem}
                  >
                    Añadir Nuevo Artículo
                  </button>
                  <button
                    className="btn btn-info btn-sm"
                    onClick={handleAddSpecialProduct}
                  >
                    Añadir Producto Especial
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Señal */}
      <div className="form-group d-flex align-items-center mt-3">
        <label htmlFor="prepaidAmount" className="mr-2 mb-0">Señal (€)</label>
        <input
          id="prepaidAmount"
          type="number"
          min="0"
          step="1"
          value={prepaidAmount ?? ''}
          onChange={(e) => {
            const isEmpty = e.target.value === '';
            const val = isEmpty
              ? (initialPrepaidAmount > 0 ? 0 : null)
              : Number(e.target.value);
            setPrepaidAmount(val);
            prepaidAmountRef.current = val;
            if (onPrepaidAmountChange) onPrepaidAmountChange(val);
          }}
          className="form-control w-auto"
          placeholder="Opcional"
        />
      </div>

      {/* Campo para el kilometraje de la orden */}
      <div className="form-group d-flex align-items-center">
        <label htmlFor="orderMileage" className="mr-2 mb-0">Kilometraje de la Orden</label>
        <input
          id="orderMileage"
          type="number"
          value={orderMileage || ''}
          onChange={(e) => setOrderMileage(e.target.value)}
          className="form-control w-auto"
        />
      </div>

      {/* Mostrar el total calculado */}
      <div className="mt-3">
        <h5 data-testid="updated-total">Importe total actualizado: {calculateTotal().toFixed(2)}€</h5>
      </div>
          {validationError && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong>⚠️ Atención:</strong> {validationError}
        </div>
      )}
      <div className="d-flex justify-content-end mt-3 gap-2">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving || !!validationError}
          data-testid="save-incident-button"
        >
          {isSaving ? 'Guardando...' : saveLabel}
        </button>
        {onCancel && (
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancelar Edición
          </button>
        )}
      </div>
    </div>
  );
};

const EditItemsInOrder = EditItemsInOrderComponent;

export default EditItemsInOrder;